import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { ArrowLeft, Clock, Calendar, User, Share2, Linkedin, Twitter, Check } from 'lucide-react';
import { useMetaTags } from '@/hooks/useMetaTags';
import blogArticles from '@/data/blogArticles';

const SITE_URL = process.env.REACT_APP_BACKEND_URL || '';

const heroStyles = {
  navy: 'linear-gradient(135deg, hsl(213 90% 12%) 0%, hsl(213 85% 20%) 50%, hsl(213 80% 28%) 100%)',
  teal: 'linear-gradient(135deg, #0D7377 0%, #094a4d 100%)',
  gold: 'linear-gradient(135deg, #8B6012 0%, #C8952A 60%, #E8B84B 100%)',
  dark: 'linear-gradient(160deg, #1B2A4A 0%, #0a1020 100%)',
};

const BlogArticlePage = () => {
  const { slug } = useParams();
  const article = blogArticles.find((a) => a.slug === slug);
  const articleUrl = article ? `${SITE_URL}/blog/${article.slug}` : '';

  useMetaTags(article ? {
    title: `${article.title} - Blog sfretire.com`,
    'og:title': article.title,
    'og:description': article.subtitle,
    'og:type': 'article',
    'og:url': articleUrl,
    'og:image': article.image,
    'twitter:card': 'summary_large_image',
    'twitter:title': article.title,
    'twitter:description': article.subtitle,
    'twitter:image': article.image,
    canonical: articleUrl,
  } : {});

  if (!article) return <Navigate to="/blog" replace />;

  const otherArticles = blogArticles.filter((a) => a.slug !== slug).slice(0, 2);
  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';
  const variant = article.heroVariant || 'navy';

  const renderBlock = (block, idx) => {
    switch (block.type) {
      case 'intro':
        return (
          <p key={idx} className="text-lg lg:text-xl leading-relaxed text-foreground/80 font-light mb-8 first-letter:text-5xl first-letter:font-serif first-letter:font-bold first-letter:text-[#1B2A4A] first-letter:float-left first-letter:mr-3 first-letter:mt-1 first-letter:leading-[0.75]">
            {block.text}
          </p>
        );
      case 'heading':
        return <h2 key={idx} className="text-2xl lg:text-[34px] font-serif font-bold text-[#1B2A4A] mt-14 mb-5 leading-tight">{block.text}</h2>;
      case 'paragraph':
        return <p key={idx} className="text-base lg:text-[17px] leading-relaxed text-foreground/75 mb-5">{block.text}</p>;
      case 'quote':
        return (
          <div key={idx} className="border-t-[3px] border-b-[3px] border-[#C8952A] py-9 my-11 text-center">
            <p className="font-serif text-xl lg:text-[28px] italic text-[#1B2A4A] leading-snug">&ldquo;{block.text}&rdquo;</p>
            {block.author && <cite className="block mt-4 text-[13px] font-sans text-[#C8952A] tracking-wider uppercase not-italic">&mdash; {block.author}</cite>}
          </div>
        );
      case 'list':
        return (
          <ul key={idx} className="my-6 space-y-3 pl-1">
            {block.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-base lg:text-[17px] text-foreground/75">
                <span className="mt-2 w-2 h-2 rounded-full bg-[#C8952A] flex-shrink-0" />
                <span className="leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        );
      case 'cardgrid':
        return (
          <div key={idx} className="grid grid-cols-1 sm:grid-cols-3 gap-5 my-9">
            {block.cards.map((card, i) => (
              <div key={i} className="bg-white border border-[#e2ddd6] border-t-[3px] border-t-[#1B2A4A] p-6 rounded-sm">
                <div className={`font-serif text-4xl font-black leading-none ${card.color === 'teal' ? 'text-[#0D7377] text-xl' : card.color === 'gold' ? 'text-[#C8952A] text-xl' : card.color === 'navy' ? 'text-[#1B2A4A] text-xl' : 'text-[#1B2A4A]'}`}>{card.value}</div>
                <div className="text-[13px] text-[#4a4a4a] mt-1.5 font-sans">{card.label}</div>
              </div>
            ))}
          </div>
        );
      case 'steps':
        return (
          <ol key={idx} className="list-none p-0 my-8 space-y-7 counter-reset-step">
            {block.items.map((step, i) => (
              <li key={i} className="flex gap-5 items-start">
                <span className="min-w-[44px] h-[44px] bg-[#1B2A4A] text-white rounded-full flex items-center justify-center font-serif text-lg font-bold flex-shrink-0">{i + 1}</span>
                <div>
                  <strong className="block font-sans font-semibold text-[#1B2A4A] mb-1">{step.title}</strong>
                  <span className="text-[#4a4a4a] text-base leading-relaxed">{step.text}</span>
                </div>
              </li>
            ))}
          </ol>
        );
      case 'checklist':
        return (
          <ul key={idx} className="list-none p-0 my-6">
            {block.items.map((item, i) => (
              <li key={i} className="py-2.5 pl-9 relative border-b border-[#e2ddd6] text-[17px] leading-relaxed text-foreground/80">
                <Check className="absolute left-0 top-3.5 w-5 h-5 text-[#0D7377]" strokeWidth={3} />
                {item}
              </li>
            ))}
          </ul>
        );
      case 'callout':
        return (
          <div key={idx} className="bg-[#F9F5EE] border-l-4 border-[#C8952A] py-6 px-7 my-9 italic text-[#4a4a4a] text-base leading-relaxed">
            {block.text}
          </div>
        );
      case 'stathighlight':
        return (
          <div key={idx} className="bg-[#1B2A4A] text-white py-12 px-10 my-11 -mx-10 text-center">
            <div className="font-serif text-6xl lg:text-8xl font-black text-[#E8B84B] leading-none">{block.value}</div>
            <div className="font-sans text-lg text-white/75 mt-3">{block.label}</div>
          </div>
        );
      case 'storybox':
        return (
          <div key={idx} className="bg-gradient-to-br from-[#0D7377] to-[#094a4d] p-10 my-10 rounded-sm text-white">
            <div className="font-serif text-2xl font-bold mb-2">{block.name}</div>
            <div className="font-sans text-[13px] text-white/60 tracking-wider uppercase mb-5">{block.role}</div>
            {block.paragraphs.map((p, i) => <p key={i} className="text-white/85 mb-4 last:mb-0">{p}</p>)}
          </div>
        );
      case 'table':
        return (
          <div key={idx} className="overflow-x-auto my-8">
            <table className="w-full border-collapse">
              <thead>
                <tr>{block.headers.map((h, i) => <th key={i} className="bg-[#1B2A4A] text-white py-3.5 px-5 font-sans text-sm text-left">{h}</th>)}</tr>
              </thead>
              <tbody>
                {block.rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 === 1 ? 'bg-[#F9F5EE]' : ''}>
                    {row.cells.map((cell, ci) => {
                      const style = row.styles?.[ci] || '';
                      const cls = style === 'good' ? 'text-[#0D7377] font-semibold' : style === 'bad' ? 'text-[#C0392B]' : style === 'bold' ? 'font-semibold' : 'text-[#4a4a4a]';
                      return <td key={ci} className={`py-3.5 px-5 border-b border-[#e2ddd6] text-base ${cls}`}>{cell}</td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      case 'cta':
        return (
          <div key={idx} className="bg-[#C8952A] py-10 px-10 my-12 -mx-10 text-center">
            <h3 className="font-serif text-2xl text-[#1B2A4A] italic mb-2">{block.title}</h3>
            <p className="text-[#1B2A4A]/75 text-[15px] mb-6">{block.text}</p>
            <Link to={block.link} className="inline-block bg-[#1B2A4A] text-white py-3.5 px-8 rounded font-sans font-medium text-sm tracking-wide hover:bg-[#0e1a30] transition-all hover:-translate-y-0.5">
              {block.linkText} &rarr;
            </Link>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFAF5]">
      <Navbar />

      <main className="pt-24">
        {/* Hero */}
        <header
          data-testid="article-header"
          className="relative overflow-hidden min-h-[420px] flex flex-col justify-end py-20 px-6 lg:px-16"
          style={{ background: heroStyles[variant] }}
        >
          <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'1\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }} />
          {/* Decorative circles */}
          <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-white/[0.04]" />
          <div className="absolute -bottom-10 -left-10 w-[200px] h-[200px] rounded-full bg-white/[0.03]" />
          {/* Article number */}
          {article.articleNumber && (
            <div className="absolute right-10 top-8 font-serif text-[140px] font-black text-transparent leading-none select-none hidden lg:block" style={{ WebkitTextStroke: '1px rgba(255,255,255,0.08)' }}>
              {article.articleNumber}
            </div>
          )}

          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="relative z-10 max-w-4xl">
            <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-6 transition-colors">
              <ArrowLeft className="w-4 h-4" />
              Retour au blog
            </Link>

            <div className="font-sans text-[11px] font-semibold tracking-[0.18em] uppercase text-white/60 mb-4">
              {article.kicker || article.category}
            </div>

            <h1 className="font-serif text-3xl sm:text-4xl lg:text-[58px] font-black text-white leading-[1.1] tracking-tight mb-5">
              {article.title}
            </h1>

            <p className="text-lg text-white/50 mb-6 max-w-2xl">{article.subtitle}</p>

            <div className="flex flex-wrap items-center gap-4 text-[13px] text-white/50 font-sans">
              <span className="flex items-center gap-2"><User className="w-4 h-4" />{article.author.name}</span>
              <span className="flex items-center gap-2"><Calendar className="w-4 h-4" />{article.date}</span>
              <span className="bg-white/10 border border-white/20 px-3 py-1 rounded-full text-white/80 text-xs">{article.readTime} de lecture</span>
              <span className="bg-white/10 border border-white/20 px-3 py-1 rounded-full text-white/80 text-xs">{article.category}</span>
            </div>
          </motion.div>
        </header>

        {/* Hero Image */}
        <div className="max-w-4xl mx-auto px-4 lg:px-8 -mt-6 relative z-20 mb-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}>
            <div className="rounded-2xl overflow-hidden shadow-xl">
              <img src={article.image} alt={article.title} data-testid="article-hero-image" className="w-full h-64 lg:h-96 object-cover" />
            </div>
          </motion.div>
        </div>

        {/* Content */}
        <article className="max-w-[720px] mx-auto px-6 lg:px-10 pb-16" data-testid="article-content">
          {/* Share bar */}
          <div className="flex items-center justify-between mb-10 pb-6 border-b border-[#e2ddd6] flex-wrap gap-3">
            <span className="font-sans text-xs font-semibold tracking-wider uppercase text-[#4a4a4a]">Partager</span>
            <div className="flex gap-2">
              <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`} target="_blank" rel="noopener noreferrer" data-testid="share-linkedin" className="px-4 py-1.5 rounded-sm border border-[#0077B5] text-[#0077B5] text-xs font-sans font-medium hover:bg-[#0077B5] hover:text-white transition-colors">LinkedIn</a>
              <a href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(article.title)}`} target="_blank" rel="noopener noreferrer" data-testid="share-twitter" className="px-4 py-1.5 rounded-sm border border-[#1DA1F2] text-[#1DA1F2] text-xs font-sans font-medium hover:bg-[#1DA1F2] hover:text-white transition-colors">Twitter</a>
            </div>
          </div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.3 }}>
            {article.content.map((block, idx) => renderBlock(block, idx))}
          </motion.div>

          {/* Author */}
          <div className="mt-16 p-6 lg:p-8 rounded-xl border border-[#e2ddd6] bg-white" data-testid="author-card">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-full flex items-center justify-center text-white font-serif font-bold text-lg" style={{ background: heroStyles[variant] }}>
                {article.author.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
              </div>
              <div>
                <div className="font-semibold text-foreground">{article.author.name}</div>
                <div className="text-sm text-[#4a4a4a]">{article.author.role}</div>
              </div>
            </div>
          </div>
        </article>

        {/* Related */}
        {otherArticles.length > 0 && (
          <section className="bg-[#F9F5EE] py-16" data-testid="related-articles">
            <div className="max-w-4xl mx-auto px-4 lg:px-8">
              <h2 className="text-2xl font-serif font-bold text-[#1B2A4A] mb-8 text-center">A lire egalement</h2>
              <div className="grid md:grid-cols-2 gap-8">
                {otherArticles.map((r) => (
                  <Link key={r.slug} to={`/blog/${r.slug}`} data-testid={`related-${r.slug}`} className="group block bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-md border border-[#e2ddd6] transition-all duration-300 hover:-translate-y-1">
                    <div className="relative h-44 overflow-hidden">
                      <img src={r.image} alt={r.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    </div>
                    <div className="p-5">
                      <span className="text-xs font-semibold text-[#C8952A] uppercase tracking-wide">{r.category}</span>
                      <h3 className="text-lg font-serif font-bold text-[#1B2A4A] mt-1 mb-2 group-hover:text-[#0D7377] transition-colors leading-snug">{r.title}</h3>
                      <p className="text-sm text-[#4a4a4a] line-clamp-2">{r.excerpt}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* Footer CTA */}
        <section className="py-16" style={{ background: heroStyles.dark }}>
          <div className="max-w-2xl mx-auto px-4 lg:px-8 text-center">
            <h2 className="text-2xl lg:text-3xl font-serif font-bold text-white mb-3">Valorisez votre expertise</h2>
            <p className="text-white/60 mb-6 text-sm">Rejoignez la communaute sfretire.com et donnez une nouvelle dimension a votre retraite.</p>
            <Link to="/retraites" data-testid="article-cta-join" className="inline-flex items-center gap-2 px-8 py-3 rounded-lg bg-[#C8952A] text-white font-semibold hover:bg-[#b8973f] transition-colors">
              Rejoindre la communaute
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogArticlePage;
